import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import {
    type Descendant,
    type BaseSelection,
    Editor,
    type BaseRange,
    Range,
    Transforms,
    Element as SlateElement,
} from 'slate';
import { Slate, Editable, ReactEditor } from 'slate-react';
import {
    type RenderElementProps,
    type SlateBlock,
    type TableBlock,
    renderBlockElement,
    renderLeafElement,
} from '@atlas/renderer';
import createEditorInstance from '@atlas/tool/box/slate/createEditorInstance';
import createTextBlock from '@atlas/tool/box/slate/createTextBlock';
import handleKeyDownEvent from '@atlas/tool/box/slate/handleKeyDownEvent';
import useLastRef from '@atlas/tool/box/hooks/useLastRef';
import showFloatingElement from '@atlas/tool/box/dom/showFloatingElement';
import type { TExample } from '@atlas/uicorn/preview/modules';
import type { TEditorFeature } from '@atlas/tool/box/slate/editorFeatures';
import Dialog from '@atlas/uicorn/lib/Display/Dialog';
import useRefCallback from 'use-ref-callback';
import translateFeaturesToRestrictions from '@atlas/tool/box/slate/translateFeaturesToRestrictions';
import matchNode from '@atlas/tool/box/slate/matchNode';
import fixEmptyChildren from '@atlas/tool/box/slate/fixEmptyChildren';
import createNodeResizeHandler from '@atlas/tool/box/slate/createNodeResizeHandler';
import TrashIcon from '@atlas/uicorn/lib/Icon/TrashIcon';
import IconButtonsGroup from '@atlas/uicorn/lib/Control/IconButtonsGroup';
import normalizeEditorChildren from '@atlas/tool/box/slate/normalizeEditorChildren';
import { isEqual } from 'lodash-es';

const RichInput = React.memo(
    React.forwardRef<TRichInputRef, TRichInputProps>(function RichInput(
        props: TRichInputProps,
        ref,
    ): React.ReactElement {
        const {
            children,
            className,
            inputClassName,
            style,
            editor,
            features,
            disabled,
            value,
            defaultValue,
            buildContext,
            blockMenu,
            onChange,
            onSelect,
            onKeyDown,
            onMouseDown,
            ...editorProps
        } = props;

        const onChangeRef = useLastRef(onChange);
        const featuresRef = useLastRef<TEditorFeature[] | undefined | null>(features);

        const localEditor = useMemo<Editor>(() => {
            const currentEditor =
                editor ??
                createEditorInstance({
                    restrictions: features && translateFeaturesToRestrictions(features),
                    maxImageHeight,
                });
            const { onKeyDown } = currentEditor;
            currentEditor.onKeyDown = (event) => {
                if (handleKeyDownEvent(currentEditor, event, featuresRef.current ?? undefined)) return;
                onKeyDown?.(event);
            };
            return currentEditor;
        }, [editor]);
        useImperativeHandle(ref, () => localEditor, [localEditor]);
        const localEditorRef = useRef(localEditor);

        useEffect(() => {
            if (!value) return;
            if (value === localEditor.children) return;

            // Mute onChange before setting passed value
            const outerOnChange = onChangeRef.current;
            onChangeRef.current = undefined;

            localEditor.children = fixEmptyChildren(value);
            localEditor.onChange();

            onChangeRef.current = outerOnChange;
        }, [value]);

        const contextKeyDownHandlerRef = useRef<null | React.KeyboardEventHandler>(null);

        const onInlineMouseDown = useRefCallback<React.MouseEventHandler<HTMLDialogElement>>(() => {
            // Prevent Safari from losing input focus when clicking on the inline element
            if (/^((?!chrome|android).)*safari/i.test(window.navigator.userAgent)) {
                setTimeout(() => {
                    ReactEditor.focus(localEditorRef.current);
                });
            }
        });

        const [inline, setInline] = useState<React.ReactElement | null>(null);
        const closeInline = useRefCallback(() => {
            setInline(null);
            ReactEditor.focus(localEditorRef.current);
        });
        // TODO: Extract and optimize (maybe call ref of the child component to set the value, so the editor doesn't re-render)
        const refreshInline = useCallback(
            (change: 'value' | 'selection') => {
                contextKeyDownHandlerRef.current = null;
                if (!buildContext) return setInline(null);

                // Ensure that editor is focused
                const editorDomNode = getEditorDomElement(localEditor);
                if (!editorDomNode) return setInline(null);
                if (document.activeElement !== editorDomNode) return setInline(null);

                // Ensure that selection is inside of the editor
                const domSelection = window.getSelection();
                if (!domSelection || !domSelection.anchorNode || !domSelection.focusNode) return setInline(null);
                if (!ReactEditor.hasDOMNode(localEditor, domSelection.anchorNode)) return setInline(null);
                if (!ReactEditor.hasDOMNode(localEditor, domSelection.focusNode)) return setInline(null);

                const [inlineElement, keyDownHandler] = buildContext(localEditor, closeInline, change) ?? [];
                contextKeyDownHandlerRef.current = keyDownHandler ?? null;
                if (!inlineElement) return setInline(null);

                let unsubscribeFloating: (() => void) | void;

                const positionInline = (anchorElement: HTMLElement) => {
                    unsubscribeFloating?.();

                    if (!anchorElement) return;

                    const inlineContainerElement = anchorElement.parentElement;
                    if (!inlineContainerElement) return;

                    const container = containerRef.current;
                    if (!container) return;

                    unsubscribeFloating = showFloatingElement(inlineContainerElement, container, {
                        refreshInterval: 300,
                    });
                };

                setInline(
                    <SInline onMouseUp={stopPropagation} onMouseDown={onInlineMouseDown}>
                        {inlineElement}
                        <SInlineAnchor key={Math.random()} ref={positionInline} />
                    </SInline>,
                );
            },
            [buildContext, closeInline, localEditor, onInlineMouseDown],
        );

        const [initialValue] = useState<TRichInputValue>(() => {
            const val = value || defaultValue || [createTextBlock()];
            const normalized = normalizeEditorChildren(val);
            if (!isEqual(normalized, localEditor.children)) {
                console.error('RichInput: initialValue is not normalized', localEditor.children);
            }
            return normalized;
        });
        const lastValueRef = useRef(initialValue);
        const lastSelectionRef = useRef(localEditor.selection);

        const handleValueChange = useRefCallback((value: TRichInputValue) => {
            if (lastValueRef.current !== value) {
                onChangeRef.current?.(value);
                lastValueRef.current = value;
                refreshInline('value');
            }
        });

        const handleSelectionChange = useRefCallback((selection: BaseSelection) => {
            if (lastSelectionRef.current !== selection) {
                onSelect?.(selection);
                lastSelectionRef.current = selection;
                refreshInline('selection');
                // closeInline();

                setTimeout(() => {
                    // Timeout is fixing image focusing
                    const container = ReactEditor.toDOMNode(localEditor, localEditor);
                    if (container) markFocusedElements(container);
                });
            }

            setTimeout(() => {
                // Timeout is fixing image focusing
                const container = ReactEditor.toDOMNode(localEditor, localEditor);
                if (container) markFocusedElements(container);
            });
        });

        const handleKeyDown = useRefCallback<React.KeyboardEventHandler<HTMLDivElement>>((event) => {
            contextKeyDownHandlerRef.current?.(event);
            if (event.defaultPrevented) return;

            onKeyDown?.(event);
            if (event.defaultPrevented) return;

            localEditorRef.current.onKeyDown(event);
        });

        const containerRef = useRef<HTMLDivElement>(null);

        const handleClick = useRefCallback<React.MouseEventHandler<HTMLDivElement>>((event) => {
            if (
                !ReactEditor.isFocused(localEditor) &&
                (event.target === event.currentTarget || event.currentTarget.contains(event.target as Node))
            ) {
                ReactEditor.focus(localEditor);
            }
        });

        const renderBlockElement = useCallback(
            (props: RenderElementProps) => {
                if (!blockMenu) {
                    if (props.element.type === 'image') {
                        const onClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
                            const el = event.currentTarget.closest('.atlas-block-image');
                            if (!el) return;
                            const node = ReactEditor.toSlateNode(localEditor, el);
                            Transforms.removeNodes(localEditor, { at: ReactEditor.findPath(localEditor, node) });
                        };
                        return renderElement(props, {
                            editing: true,
                            extra: (
                                <SFigureActionButtons items={[{ icon: <TrashIcon />, tooltip: 'Remove', onClick }]} />
                            ),
                        });
                    }
                    return renderElement(props, { editing: true });
                }
                return React.createElement('div', { className: 'atlas-block-container' }, [
                    renderElement(props, { editing: true }),
                    blockMenu({ node: { type: 'paragraph', children: [] }, editor: localEditor }),
                ]);
            },
            [blockMenu, localEditor],
        );

        const handleImageResizeRight = useRefCallback<React.MouseEventHandler<HTMLElement>>(
            createNodeResizeHandler(localEditor, 'atlas-block-image__resize-right', {
                minSize: minImageSize,
                maxHeight: editor?.maxImageHeight ?? maxImageHeight,
                direction: 'right',
            }),
        );

        const handleImageResizeLeft = useRefCallback<React.MouseEventHandler<HTMLElement>>(
            createNodeResizeHandler(localEditor, 'atlas-block-image__resize-left', {
                minSize: minImageSize,
                maxHeight: editor?.maxImageHeight ?? maxImageHeight,
                direction: 'left',
            }),
        );

        const handleImageResizeBottom = useRefCallback<React.MouseEventHandler<HTMLElement>>(
            createNodeResizeHandler(localEditor, 'atlas-block-image__resize-bottom', {
                minSize: minImageSize,
                maxHeight: editor?.maxImageHeight ?? maxImageHeight,
                direction: 'bottom',
            }),
        );

        const handleEmbedResize = useRefCallback<React.MouseEventHandler<HTMLElement>>((event) => {
            const resizeEl = event.target;
            if (!(resizeEl instanceof HTMLElement)) return;
            if (!resizeEl.classList.contains('atlas-block-embed__resize')) return;

            event.preventDefault();
            const MIN_EMBED_HEIGHT = 100;
            const parentEl = resizeEl.parentElement as HTMLElement;
            const initialEmbedHeight = parentEl.offsetHeight;
            const initialCursorY = event.clientY;
            const handleMove = (event: MouseEvent) => {
                const change = event.clientY - initialCursorY;
                const modifiedHeight = initialEmbedHeight + change;
                const height = modifiedHeight > MIN_EMBED_HEIGHT ? modifiedHeight : MIN_EMBED_HEIGHT;
                Transforms.setNodes(
                    localEditor,
                    { height },
                    {
                        match: (node) => SlateElement.isElement(node) && node.type === 'embed',
                    },
                );
            };
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', () => window.removeEventListener('mousemove', handleMove), {
                once: true,
            });
        });

        const handleTableColumnResize = useRefCallback<React.MouseEventHandler<HTMLElement>>((event) => {
            const resizeEl = event.target;
            if (!(resizeEl instanceof HTMLElement)) return;
            if (!resizeEl.classList.contains('atlas-block-table-cell__resize')) return;

            const tableNode = matchNode<TableBlock>(
                localEditor,
                (node) => SlateElement.isElement(node) && node.type === 'table',
            );
            if (!tableNode) return;

            const parentEl = resizeEl.parentElement;
            if (!parentEl?.parentElement) return;

            const columnIndex = Array.from(parentEl.parentElement.children).indexOf(parentEl);
            if (columnIndex < 0) return;

            event.preventDefault();
            const initialCellWidth = parentEl.offsetWidth;
            const initialCursorX = event.clientX;

            const handleMove = (event: MouseEvent) => {
                const change = event.clientX - initialCursorX;
                const modifiedWidth = initialCellWidth + change;
                const width = modifiedWidth > minColumnWidth ? modifiedWidth : minColumnWidth;
                const columns = tableNode.columns ? [...tableNode.columns] : new Array(columnIndex).fill({});
                columns[columnIndex] = { ...columns[columnIndex], width };
                Transforms.setNodes(
                    localEditor,
                    { columns },
                    {
                        match: (node) => SlateElement.isElement(node) && node.type === 'table',
                    },
                );
            };
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', () => window.removeEventListener('mousemove', handleMove), {
                once: true,
            });
        });

        const handleMouseDown = useRefCallback<React.MouseEventHandler<HTMLDivElement>>((event) => {
            onMouseDown?.(event);
            if (event.defaultPrevented) return;
            handleImageResizeRight(event);
            handleImageResizeLeft(event);
            handleImageResizeBottom(event);
            handleEmbedResize(event);
            handleTableColumnResize(event);
        });

        const inputElement = (
            <SRichInput className={className} style={style} onClick={handleClick} ref={containerRef}>
                <SRichInputEditable
                    {...editorProps}
                    className={inputClassName}
                    renderElement={renderBlockElement}
                    renderLeaf={renderLeaf}
                    onMouseDown={handleMouseDown}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    readOnly={disabled}
                />
                {inline}
            </SRichInput>
        );

        return (
            <Slate
                editor={localEditor}
                initialValue={initialValue}
                onValueChange={handleValueChange}
                onSelectionChange={handleSelectionChange}
            >
                {children ? children(inputElement) : inputElement}
            </Slate>
        );
    }),
);

export default RichInput;

// #region Helpers

const minColumnWidth = 30;
const minImageSize = 32;
export const maxImageHeight = 400;

function stopPropagation(event: React.UIEvent) {
    event.stopPropagation();
}

function getEditorDomElement(editor: Editor): HTMLElement | null {
    try {
        return ReactEditor.toDOMNode(editor, editor) || null;
    } catch (e) {
        return null;
    }
}

const renderElement = renderBlockElement.bind(null, React.createElement, React.Fragment);

const renderLeaf = renderLeafElement.bind(null, React.createElement, React.Fragment);

const focusClassName = 'atlas-focus';

function markFocusedElements(container: HTMLElement) {
    const selection = window.getSelection();
    if (!selection?.focusNode) return;
    if (!container.contains(selection.focusNode)) return;

    container.querySelectorAll(`.${focusClassName}`).forEach((node) => node.classList.remove(focusClassName));

    let descendant = selection.focusNode;
    while (container.contains(descendant)) {
        if (
            descendant instanceof Element &&
            (descendant.classList.contains('atlas-block') || descendant.classList.contains('atlas-text'))
        ) {
            descendant.classList.add(focusClassName);
        }
        if (!descendant.parentNode) break;
        descendant = descendant.parentNode;
    }
}

// #endregion

// #region Styles

export const SFigureActionButtons = styled(IconButtonsGroup)`
    background-color: ${(p) => p.theme.color.grey0o80};
    border-radius: 0.4rem;
`;

const SInlineAnchor = styled.i`
    display: none;
`;

const SInline = styled(Dialog).attrs({ open: true, elevation: 'low' })`
    position: fixed;
    z-index: 1;
    max-height: 100%;
    max-height: min(50rem, 100%);
    max-width: 100%;
    max-width: min(40rem, 100%);
`;

const shineKeyframes = keyframes`
    to {
        opacity: 1;
        right: 210%;
    }
`;

const resizeBarWidth = '0.6rem';
const resizeBarLength = '40%';

export const SRichInputEditable = styled(Editable)`
    font: ${(p) => p.theme.font.normal};
    overflow: visible;
    opacity: 1;
    transition: opacity 0.2s ease;
    outline: none;

    &[contenteditable='false'] {
        // Disabled state
        opacity: 0.2;
    }

    & > :last-child {
        margin-bottom: 0;
    }

    & > * {
        &::before {
            content: '';
            display: block;
            overflow: hidden;
        }
    }

    .atlas-block:not(.atlas-block-paragraph) span[data-slate-placeholder='true'],
    .atlas-block-paragraph code.atlas-leaf span[data-slate-placeholder='true'] {
        visibility: hidden;
    }

    .atlas-block-container {
        position: relative;
        margin-bottom: 1em;
    }

    // Keep ↓↓ typography definitions in sync with .message-rte-html
    .atlas-block-heading-one,
    .atlas-block-heading-two {
        margin: 0;
        color: inherit;
        line-height: 1.3;

        &:not(:first-child) {
            margin-top: 1em;
        }
    }

    .atlas-block-heading-one {
        font-size: 2em;
    }

    .atlas-block-heading-two {
        font-size: 1.5em;
    }

    .atlas-block-paragraph {
        margin: 0;
        line-height: 1.6;
        text-overflow: ellipsis;
        overflow: hidden;

        &:not(:first-child) {
            margin-top: 1em;
        }
    }

    .atlas-block-quote {
        border-left: 4px solid var(--color-grey6);
        padding-left: 0.8em;
        margin: 0;
        overflow: auto;

        &:not(:first-child) {
            margin-top: 1em;
        }
    }

    .atlas-block-code {
        background: ${(p) => p.theme.color.grey6};
        color: ${(p) => p.theme.color.grey9};
        font-family: 'JetBrains Mono', Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
        padding: 1em;
        border-radius: 6px;
        tab-size: 4;
        white-space: pre;
        overflow: auto;
        margin-bottom: 0;

        &:not(:first-child) {
            margin-top: 1em;
        }
    }

    .atlas-block-icon {
        display: inline-block;
        margin: 0;
        position: relative;
    }

    .atlas-block-icon-text {
        position: absolute;
        inset: 0;
    }

    .atlas-block-callout {
        background: ${(p) => p.theme.color.grey5};
        color: ${(p) => p.theme.color.grey9};
        padding: 12px 16px;
        border-radius: 5px;
        overflow: auto;

        &:not(:first-child) {
            margin-top: 1em;
        }

        .atlas-block-icon {
            float: left;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 32px;
            height: 32px;
            background: ${(p) => p.theme.color.grey5};
            border-radius: 4px;
            margin: 0;
            cursor: pointer;
            user-select: none;
        }

        .atlas-block-paragraph {
            margin: 0;
            flex: 1 0 auto;
            padding: 4px 0;
        }

        .atlas-block-icon + .atlas-block-paragraph {
            margin-left: 44px;
        }
    }

    .atlas-block-image,
    .atlas-block-video,
    .atlas-block-video-embed {
        &:not(:first-child) {
            margin-top: 1em;
        }

        &__float-left {
            float: left;
        }

        &__float-right {
            float: right;
        }

        &__centered,
        &__float-left {
            margin-right: 2em;
        }

        &__centered,
        &__float-right {
            margin-left: 2em;
        }

        &__float-left,
        &__float-right {
            max-width: 60%;
        }
    }

    .atlas-block-video,
    .atlas-block-video-embed {
        &__centered {
            margin-left: auto;
            margin-right: auto;
        }

        &__float-left {
            margin-right: 2em;
        }

        &__float-right {
            margin-left: 2em;
        }

        &__centered,
        &__float-left,
        &__float-right {
            width: 60%;
        }
    }

    .atlas-block-image-container,
    .atlas-block-video-container {
        position: relative;
        display: inline-block;
        width: 100%;
    }

    .atlas-block-image img,
    .atlas-block-video video,
    .atlas-block-video-embed iframe,
    .atlas-block-embed iframe {
        max-width: 100%;
        /* border-radius: 8px; */
    }

    .atlas-block-embed {
        &:not(:first-child) {
            margin-top: 1em;
        }
    }

    .atlas-block-image__resize-left,
    .atlas-block-image__resize-right,
    .atlas-block-image__resize-bottom {
        display: inline-block;
        position: absolute;
        z-index: 1;
        background: ${(p) => p.theme.color.grey0};
        box-shadow: ${(p) => p.theme.shadow.low};
        border: 0.05rem solid ${(p) => p.theme.color.grey6};
        border-radius: calc(${resizeBarWidth} / 2);
    }

    &[contenteditable='false'] {
        .atlas-block-image__resize-left,
        .atlas-block-image__resize-right,
        .atlas-block-image__resize-bottom {
            display: none;
        }
    }

    .atlas-block-image__resize-left,
    .atlas-block-image__resize-right {
        width: ${resizeBarWidth};
        height: ${resizeBarLength};
        top: 50%;
        translate: 0 -50%;
        cursor: col-resize;
    }

    .atlas-block-image__resize-left {
        left: 0.4rem;
    }

    .atlas-block-image__resize-right {
        right: 0.4rem;
    }

    .atlas-block-image__resize-bottom {
        width: ${resizeBarLength};
        height: ${resizeBarWidth};
        left: 50%;
        translate: -50% 0;
        cursor: row-resize;
    }

    .atlas-block-image__resize-bottom {
        bottom: 0.4rem;
    }

    &:not(:focus-within) .atlas-block-image-container:not(:hover),
    .atlas-block-image:not(.${focusClassName}) .atlas-block-image-container:not(:hover),
    .atlas-block-image__expanded {
        .atlas-block-image__resize-left,
        .atlas-block-image__resize-right,
        .atlas-block-image__resize-bottom {
            display: none;
        }
    }

    .atlas-block-image__resize-bottom {
        // Not used now
        display: none;
    }

    .atlas-block-image__float-right .atlas-block-image__resize-right {
        display: none;
    }

    .atlas-block-image__float-left .atlas-block-image__resize-left {
        display: none;
    }

    .${focusClassName} {
        .atlas-block-embed__resize {
            display: inline-block;
            width: 110px;
            height: 16px;
            border-radius: 8px;
            position: absolute;
            bottom: 0;
            left: 50%;
            z-index: 2;
            cursor: ns-resize;
            transform: translate(-50%, 0%);

            &::before {
                content: '';
                display: block;
                position: absolute;
                border: 1.6px solid currentColor;
                border-right: 0;
                border-top: 0;
                border-left: 0;
                top: 50%;
                height: 2.5px;
                background: var(--color-grey9);
                border: 0.5px solid var(--color-grey0);
                width: 100%;
                border-radius: 2px;
            }
        }
    }

    .atlas-block-image__expanded img {
        width: 100%;
    }

    .atlas-block-image__centered {
        text-align: center;
    }

    .atlas-block-video video {
        width: 100%;
        height: auto;
    }

    .atlas-block-video-embed__youtube {
        padding-bottom: 56.3%;

        &.atlas-block-video-embed__centered,
        &.atlas-block-video-embed__float-left,
        &.atlas-block-video-embed__float-right {
            padding-bottom: 33.8%;
        }
    }

    .atlas-block-video-embed__clueso {
        padding-bottom: 56.3%;
        overflow: hidden;
    }

    .atlas-block-video-embed__clueso.atlas-block-video-embed__centered,
    .atlas-block-video-embed__clueso.atlas-block-video-embed__float-left,
    .atlas-block-video-embed__clueso.atlas-block-video-embed__float-right {
        padding-bottom: 35.8%;

        .atlas-block-video-embed-iframe {
            margin-top: -20px;
        }
    }

    @media screen and (max-width: 768px) {
        .atlas-block-video-embed__clueso {
            padding-bottom: 60%;

            .atlas-block-video-embed-iframe {
                margin-top: -20px;
            }
        }
    }

    .atlas-block-video-embed__vimeo {
        padding-bottom: 56.3%;

        &.atlas-block-video-embed__centered,
        &.atlas-block-video-embed__float-left,
        &.atlas-block-video-embed__float-right {
            padding-bottom: 33.8%;
        }
    }

    .atlas-block-video-embed__loom {
        padding-bottom: 62.5%;

        &.atlas-block-video-embed__centered,
        &.atlas-block-video-embed__float-left,
        &.atlas-block-video-embed__float-right {
            padding-bottom: 37.5%;
        }
    }

    .atlas-block-video-embed,
    .atlas-block-embed {
        position: relative;
        text-align: center;
    }

    .atlas-block-video-embed iframe,
    .atlas-block-embed .atlas-block-embed-iframe {
        border: 0;
        width: 100%;
        height: 100%;
    }

    .atlas-block-embed-text,
    .atlas-block-video-embed-text,
    .atlas-block-video-embed iframe,
    .atlas-block-embed-iframe {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    }

    .atlas-block-embed-text,
    .atlas-block-video-embed-text {
        z-index: 1;
    }

    .atlas-block-video-text {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1;
    }

    .atlas-block-icon-text,
    .atlas-block-image-text,
    .atlas-block-video-text,
    .atlas-block-video-embed-text,
    .atlas-block-embed-text {
        display: flex;
        justify-content: center;
    }

    .atlas-block-image ${SFigureActionButtons} {
        position: absolute;
        z-index: 1;
        top: 0.5em;
        right: 0.5em;
        box-shadow: ${(p) => p.theme.shadow.low};
        border: 0.05rem solid ${(p) => p.theme.color.grey6};
    }

    &:not(:focus-within),
    .atlas-block-image:not(.${focusClassName}) {
        .atlas-block-image-container:not(:hover) ${SFigureActionButtons} {
            display: none;
        }
    }

    &:focus-within .${focusClassName} {
        &.atlas-block-icon,
        & .atlas-block-embed-iframe,
        & .atlas-block-embed-html,
        &.atlas-block-video-embed iframe,
        &.atlas-block-video video,
        &.atlas-block-image img {
            box-shadow: ${(p) => p.theme.shadow.composerMediaFocus};
        }

        &.atlas-block-horizontal-line::after {
            box-shadow: ${(p) => p.theme.shadow.composerRulerFocus};
        }
    }

    &[contenteditable='false'] {
        .atlas-block-icon-text,
        .atlas-block-image-text,
        .atlas-block-video-text,
        .atlas-block-video-embed-text,
        .atlas-block-embed-text {
            display: none;
        }
    }

    .atlas-block-table {
        max-width: 100%;
        overflow: auto;

        &:not(:first-child) {
            margin-top: 1em;
        }

        &__centered table {
            margin-left: auto;
            margin-right: auto;
        }

        &__float-left {
            float: left;
            margin-right: 2em;
        }

        &__float-right {
            float: right;
            margin-left: 2em;
        }

        &.atlas-block-table__expanded > table {
            min-width: 100%;
        }
    }

    .atlas-block-table-cell {
        // TODO: @Stan set min-height for cells so empty rows doesn't look flat for readers
        border: 1px solid lightgray;
        padding: 5px 10px;
        vertical-align: top;
        position: relative;
        min-width: 85px;

        &__align-left {
            text-align: left;
        }

        &__align-right {
            text-align: right;
        }

        &__align-center {
            text-align: center;
        }
    }

    .atlas-block-table-cell__resize {
        position: absolute;
        right: 0;
        top: -1px;
        bottom: -1px;
        width: 0;
        background: #ddd;
        transition: width 0.2s ease, right 0.2s ease;
    }

    .atlas-block-table-row:first-child .atlas-block-table-cell__resize {
        border-top-left-radius: 2px;
        border-top-right-radius: 2px;
    }

    .atlas-block-table-row:last-child .atlas-block-table-cell__resize {
        border-bottom-left-radius: 2px;
        border-bottom-right-radius: 2px;
    }

    .atlas-block-table.${focusClassName} {
        .atlas-block-table-cell__resize {
            cursor: ew-resize;
            width: 7px;
            right: -3px;
        }

        &.atlas-block-table__expanded {
            .atlas-block-table-cell:last-child .atlas-block-table-cell__resize {
                display: none;
            }
        }
    }

    &[contenteditable='false'] {
        .atlas-block-table-cell__resize {
            display: none;
        }
    }

    .atlas-block-list {
        &:not(:first-child) {
            margin-top: 1em;
        }

        & > ul,
        & > ol {
            margin: 0;
            padding-left: 3em;
        }

        .atlas-block-list {
            margin-top: 0;
            padding-left: 2em;
        }
    }

    .atlas-block-list {
        & > ol {
            list-style-type: decimal;
        }

        & > ul {
            list-style-type: disc;
        }

        .atlas-block-list {
            & > ol {
                list-style-type: lower-latin;
            }

            & > ul {
                list-style-type: circle;
            }

            .atlas-block-list {
                & > ol {
                    list-style-type: lower-roman;
                }

                & > ul {
                    list-style-type: square;
                }

                .atlas-block-list {
                    & > ol {
                        list-style-type: decimal;
                    }

                    & > ul {
                        list-style-type: disc;
                    }

                    .atlas-block-list {
                        & > ol {
                            list-style-type: lower-latin;
                        }

                        & > ul {
                            list-style-type: circle;
                        }

                        .atlas-block-list {
                            & > ol {
                                list-style-type: lower-roman;
                            }

                            & > ul {
                                list-style-type: square;
                            }

                            .atlas-block-list {
                                & > ol {
                                    list-style-type: decimal;
                                }

                                & > ul {
                                    list-style-type: disc;
                                }

                                .atlas-block-list {
                                    & > ol {
                                        list-style-type: lower-latin;
                                    }

                                    & > ul {
                                        list-style-type: circle;
                                    }

                                    .atlas-block-list {
                                        & > ol {
                                            list-style-type: lower-roman;
                                        }

                                        & > ul {
                                            list-style-type: square;
                                        }

                                        .atlas-block-list {
                                            & > ol {
                                                list-style-type: decimal;
                                            }

                                            & > ul {
                                                list-style-type: disc;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    code.atlas-leaf {
        background: ${(p) => p.theme.color.atlasLeafCodeBackground};
        color: ${(p) => p.theme.color.atlasLeafCodeText};
        padding: 0.2em 0.4em;
        border-radius: 2px;
    }

    a.atlas-leaf {
        color: ${(p) => p.theme.color.blue8};
        text-decoration: underline;

        &:hover {
            text-decoration: none;
        }
    }

    .atlas-leaf[data-constant] {
        white-space: nowrap;
    }

    .atlas-leaf[data-constant$='mention'] {
        color: var(--color-blue9);
        font-weight: 500;
    }

    .atlas-leaf[data-constant$='article'] {
        color: var(--color-blue8);
        text-decoration: underline;
    }

    .atlas-leaf[data-constant$='variable'],
    .atlas-leaf[data-constant$='tool'] {
        color: ${(p) => p.theme.color.primary};
        padding: 1px 3px;
        background: ${(p) => p.theme.color.grey0};
        border-radius: 4px;
        border: 1px solid ${(p) => p.theme.color.shadowExtraLow};
    }

    .atlas-block-horizontal-line {
        &:not(:first-child) {
            margin-top: 1em;
        }

        &::after {
            content: '';
            display: block;
            background: ${(p) => p.theme.color.grey6};
            height: 1px;
        }
    }

    .atlas-block-image img,
    .atlas-block-video,
    .atlas-block-video-embed,
    .atlas-block-horizontal-line {
        cursor: default;
    }

    .atlas-block-image__placeholder {
        &::after {
            content: '';
            position: absolute;
            top: -30%;
            right: -100%;
            width: 50%;
            height: 100%;
            opacity: 0;
            transform: rotate(30deg);
            background: rgba(255, 255, 255, 0.13);
            background: linear-gradient(
                to right,
                rgba(255, 255, 255, 0.13) 0%,
                rgba(255, 255, 255, 0.13) 77%,
                rgba(255, 255, 255, 0.5) 92%,
                rgba(255, 255, 255, 0) 100%
            );
            animation: ${shineKeyframes} 2s 1s infinite;
        }
    }
`;

export const SRichInput = styled.div`
    position: relative;
    overflow: auto;

    &:not(:focus-within) ${SInline} {
        display: none;
    }
`;

// #endregion

// #region Types

export type TRichInputRef = Editor;

export type TRichInputDescendant = Descendant;

export type TRichInputValue = TRichInputDescendant[];

export type TRichInputChangeHandler = (value: TRichInputValue) => void;

export type TRichInputSelectHandler = (selection: BaseSelection) => void;

export type TRichInputRange = BaseRange;

export type TRichInputContextBuilder = (
    editor: Editor,
    close: () => void,
    change?: 'value' | 'selection',
    onMention?: (isCustomer?: boolean) => void,
) => [view: React.ReactNode, keyDownHandler?: null | React.KeyboardEventHandler] | null | undefined;

export type TRichInputBlockMenu = ({ node, editor }: { node: SlateBlock; editor: Editor }) => React.ReactNode;

export type TRichInputProps = Pick<
    React.ComponentProps<typeof Editable>,
    | 'className'
    | 'style'
    | 'disabled'
    | 'placeholder'
    | 'onFocus'
    | 'onBlur'
    | 'onKeyDown'
    | 'onMouseDown'
    | 'onDOMBeforeInput'
    | 'onPaste'
    | 'scrollSelectionIntoView'
> & {
    // Add class name to the textbox
    inputClassName?: string;

    // Default behavior of the `onChange` is changed to separate changes of value and selection
    onChange?: TRichInputChangeHandler;
    onSelect?: TRichInputSelectHandler;

    // Temporary option to pass configured editor (need to adopt older RE now)
    editor?: Editor;

    // Using `value` may cause performance issues.
    // Prefer `defaultValue` and `editor` properties to set and modify the content
    value?: TRichInputValue;
    defaultValue?: TRichInputValue;

    // This property can be used to customize what is rendered within <Slate> scope
    children?: (input?: React.ReactElement) => React.ReactNode;

    // Limits block and marks that can be used in the editor
    features?: TEditorFeature[] | null;

    // Display content next to the selection
    buildContext?: TRichInputContextBuilder;

    // Define context menu for block
    blockMenu?: TRichInputBlockMenu;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        const style: React.CSSProperties = {
            width: '400px',
            minHeight: '50px',
            maxHeight: '200px',
            border: '1px dashed #ccc',
        };

        return (
            <RichInput
                defaultValue={[{ type: 'paragraph', children: [{ text: 'Hello World!' }] }]}
                onChange={props.onChange}
                onSelect={props.onSelect}
                placeholder="Type in..."
                style={style}
            />
        );
    },
    handlers: {
        onChange: 'console',
        onSelect: 'console',
    },
};

export const InlineExample: TExample = {
    render(props) {
        const style: React.CSSProperties = useMemo(
            () => ({
                width: '400px',
                minHeight: '50px',
                maxHeight: '200px',
                border: '1px dashed #ccc',
            }),
            [],
        );

        const defaultValue = useMemo<TRichInputValue>(
            () => [
                {
                    type: 'paragraph',
                    children: [
                        {
                            text: 'Amet anim aliquip do labore anim et Lorem officia sint sint. Anim mollit do reprehenderit est dolore qui et aliquip et qui aliqua deserunt consectetur sunt. Aute commodo ut aliqua cupidatat minim nisi aliqua commodo officia ut qui commodo dolore Lorem officia. Ut dolor cupidatat incididunt exercitation aliqua consequat nostrud sit excepteur. Exercitation reprehenderit nostrud anim dolore non deserunt consectetur fugiat cupidatat laboris laborum. Eiusmod incididunt incididunt esse consectetur culpa dolore laborum eiusmod proident. Qui aliqua ea reprehenderit cillum ullamco minim eiusmod laborum culpa excepteur proident magna. Reprehenderit ipsum ipsum Lorem eu amet reprehenderit adipisicing pariatur.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    children: [
                        {
                            text: 'Proident ipsum veniam id pariatur elit sint cillum officia ipsum cupidatat do incididunt. Tempor adipisicing cupidatat dolore adipisicing elit excepteur exercitation sint ea proident. Nulla et adipisicing cupidatat fugiat enim tempor tempor Lorem. Ut voluptate elit sint minim quis dolor sit elit eu quis laboris eiusmod nostrud minim occaecat. Dolore exercitation consequat ullamco occaecat aute. Ut deserunt laboris et aliquip consequat magna. Incididunt eu labore eiusmod qui enim ipsum sint anim cillum ut quis. Lorem laborum exercitation consectetur culpa quis voluptate labore nisi nostrud ea id.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    children: [
                        {
                            text: 'Veniam duis mollit id est dolor proident ad pariatur id ipsum nostrud voluptate ad exercitation. Nulla qui esse do pariatur excepteur. Veniam voluptate irure culpa qui velit in laboris consequat. Eiusmod velit nostrud commodo reprehenderit veniam dolore ex irure laboris qui et pariatur pariatur. Eiusmod esse do eu ut tempor quis eiusmod. Dolor cillum adipisicing et minim magna aliquip qui pariatur reprehenderit elit commodo ullamco quis. Cillum culpa enim labore officia eu.',
                        },
                    ],
                },
            ],
            [],
        );

        const buildContext = useCallback<TRichInputContextBuilder>((editor) => {
            const { selection } = editor;
            if (!selection) return;

            const tooltipStyle: React.CSSProperties = {
                background: 'black',
                color: 'white',
                padding: '0 0.4rem',
                borderRadius: '0.5rem',
                fontSize: '1.1rem',
                maxWidth: '20.0rem',
            };

            if (Range.isCollapsed(selection)) {
                const precedingText = Editor.string(editor, {
                    ...selection,
                    focus: Editor.start(editor, selection.anchor.path.slice(0, -1)),
                });

                const precedingWordMatch = precedingText.match(/\w+$/);
                if (!precedingWordMatch) return;

                const [precedingWord] = precedingWordMatch;
                if (!precedingWord) return;

                return [
                    <div style={tooltipStyle}>
                        Preceding word: <strong>{precedingWord}</strong>
                    </div>,
                ];
            }

            const selectedString = Editor.string(editor, selection);

            return [
                <div style={tooltipStyle}>
                    Selected string: <strong>{selectedString}</strong>
                </div>,
            ];
        }, []);

        return (
            <RichInput
                defaultValue={defaultValue}
                onChange={props.onChange}
                onSelect={props.onSelect}
                placeholder="Type in..."
                style={style}
                buildContext={buildContext}
            />
        );
    },
    handlers: {
        onChange: 'console',
        onSelect: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, test, expect } = import.meta.vitest;
    describe('RichInput', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        test('matches snapshot', async () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
